import clsx from 'clsx';
import { CornerUpLeft, Workflow } from 'lucide-react';
import type { Attachment, UserRef, WorkflowRunStatus } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import type { BadgeTone } from './ui';

export const RUN_LABEL: Record<WorkflowRunStatus, TranslationKey> = {
  ACTIVE: 'workflows.run.ACTIVE',
  COMPLETED: 'workflows.run.COMPLETED',
  STOPPED: 'workflows.run.STOPPED',
};

export type LevelProgress = 'done' | 'current' | 'waiting' | 'skipped' | 'stopped' | 'unreached';

export const PROGRESS_LABEL: Record<LevelProgress, TranslationKey> = {
  done: 'workflows.progress.done',
  current: 'workflows.progress.current',
  waiting: 'workflows.progress.waiting',
  skipped: 'workflows.progress.skipped',
  stopped: 'workflows.progress.stopped',
  unreached: 'workflows.progress.unreached',
};

/**
 * `task.workflow` — the journey a task is one step of, as `GET /tasks/:id` hands
 * it out. Built on the task since 2026-10-15, it has no name; one started before
 * from a saved workflow keeps that name.
 */
export interface WorkflowJourney {
  id: string;
  status: WorkflowRunStatus;
  currentLevel: number;
  name: string | null;
  nameAr: string | null;
  createdBy: UserRef;
  levels: Array<{
    level: number;
    progress: LevelProgress;
    /** Everyone on the level, as it was built — `isActive` false for someone who has left since. */
    people: Array<UserRef & { isActive?: boolean }>;
    tasks: Array<{
      id: string;
      title: string;
      status: string;
      dueAt: string | null;
      completedAt: string | null;
      assignee: UserRef | null;
      /** Out for a check (2026-10-15): the step's own person — `assignee` is checking it. */
      returnTo: UserRef | null;
      attachments: Attachment[];
    }>;
    /** Whose the steps on this level are that this person may not open — a step is private. */
    others?: UserRef[];
  }>;
}

export function runTone(status: WorkflowRunStatus): BadgeTone {
  return status === 'COMPLETED' ? 'success' : status === 'STOPPED' ? 'warning' : 'info';
}

/**
 * How far along its workflow a task is: "Level 2/3" — "Website page · 2/3" for
 * one started from a saved workflow before workflows were built on the task
 * (2026-10-15). A step out for a check says whose it is: it goes back to them.
 * For the task lists and the board, where a row has little room.
 */
export function WorkflowStep({
  task,
  className,
}: {
  task: {
    workflowLevel?: number | null;
    workflowRun?: { levels: unknown; name?: string | null; nameAr?: string | null } | null;
    returnTo?: UserRef | null;
  };
  className?: string;
}) {
  const { t, locale, localized } = useI18n();
  if (!task.workflowRun || !task.workflowLevel) return null;
  const total = Array.isArray(task.workflowRun.levels) ? task.workflowRun.levels.length : task.workflowLevel;
  const run = task.workflowRun;
  const name = run.name ? localized({ name: run.name, nameAr: run.nameAr }) : t('tasks.workflow');
  const step = t('workflows.levelOf', { n: formatNumber(task.workflowLevel, locale), total: formatNumber(total, locale) });
  const owner = task.returnTo ? localized(task.returnTo) : null;
  return (
    <span
      className={clsx('inline-flex min-w-0 items-center gap-1 text-caption text-muted', className)}
      title={`${t('tasks.workflow')}: ${run.name ? `${name} — ` : ''}${step}`}
    >
      <Workflow className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="truncate">{name}</span>
      <span className="shrink-0 tabular">
        · {formatNumber(task.workflowLevel, locale)}/{formatNumber(total, locale)}
      </span>
      {owner && (
        <span
          className="inline-flex shrink-0 items-center gap-0.5 text-warning-fg dark:text-warning-dark"
          title={t('tasks.return.listHint', { name: owner })}
        >
          <CornerUpLeft className="h-3.5 w-3.5 flip-rtl" aria-hidden />
          {owner}
        </span>
      )}
    </span>
  );
}
